import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, UserRound, Database, Loader2, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import TechDialog from "@/components/TechDialog";
import InviteLinkDialog from "@/components/InviteLinkDialog";
import { PERM_LABELS } from "@/components/TechDialog";

export default function Techs() {
  const [users, setUsers] = useState(null);
  const [dbs, setDbs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [inviting, setInviting] = useState(null);

  const newLink = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/invite`);
      setInviting(data);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const load = () =>
    Promise.all([api.get("/users"), api.get("/databases")])
      .then(([u, d]) => { setUsers(u.data); setDbs(d.data); })
      .catch((e) => toast.error(formatError(e)));

  useEffect(() => { load(); }, []);

  const remove = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success(`${toDelete.name} removed`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
    setToDelete(null);
  };

  const dbName = (id) => dbs.find((d) => d.id === id)?.name;
  const techs = users?.filter((u) => u.role !== "admin") || [];

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Techs & access</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Invite techs, tick the databases each one may open, and choose which parts of the app they can use.
          </p>
          <p className="text-xs text-muted-foreground mt-2" data-testid="admin-stats">
            {techs.length} tech{techs.length === 1 ? "" : "s"} · {techs.filter((u) => u.invite_token).length} invite(s) pending · {dbs.length} database{dbs.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setEditing({})} data-testid="add-tech-button" className="hover:bg-blue-600 hover:text-white"><Plus size={16} /> Invite tech</Button>
        </div>
      </div>


      {!users ? <Loader2 className="mt-8 animate-spin text-primary" /> : (
        <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tech-list">
          {techs.map((u, i) => (
            <div key={u.id} className="border rounded-lg bg-card p-5 space-y-4" data-testid={`tech-card-${i}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-md bg-primary/15 text-primary flex items-center justify-center"><UserRound size={18} /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg font-bold truncate" data-testid={`tech-name-${i}`}>{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest mt-1 text-muted-foreground">
                    {u.invite_token ? <span className="text-amber-400" data-testid={`tech-invite-pending-${i}`}>Invite pending</span> : u.has_password ? "Password + Google" : "Google only"} · <span className={u.active ? "text-emerald-400" : "text-red-400"}>{u.active ? "Active" : "Disabled"}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => (u.invite_token ? setInviting(u) : newLink(u))} className="text-muted-foreground hover:text-cyan-400" data-testid={`invite-tech-${i}`} aria-label="Invite link" title={u.invite_token ? "Show invite link" : "Send a new invite link"}><Send size={15} /></button>
                  <button onClick={() => setEditing(u)} className="text-muted-foreground hover:text-primary" data-testid={`edit-tech-${i}`} aria-label="Edit tech"><Pencil size={15} /></button>
                  <button onClick={() => setToDelete(u)} className="text-muted-foreground hover:text-red-400" data-testid={`delete-tech-${i}`} aria-label="Remove tech"><Trash2 size={15} /></button>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Databases</p>
                <div className="flex flex-wrap gap-1.5" data-testid={`tech-dbs-${i}`}>
                  {u.db_ids.filter(dbName).length === 0 && <span className="text-xs text-amber-400">No databases yet</span>}
                  {u.db_ids.filter(dbName).map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs"><Database size={11} /> {dbName(id)}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Can use</p>
                <div className="flex flex-wrap gap-1.5" data-testid={`tech-perms-${i}`}>
                  {Object.entries(PERM_LABELS).filter(([k]) => u.perms?.[k]).map(([k, l]) => (
                    <span key={k} className="inline-flex items-center gap-1 rounded bg-primary/10 text-primary px-2 py-0.5 text-xs"><ShieldCheck size={11} /> {l}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {techs.length === 0 && <p className="text-muted-foreground" data-testid="tech-empty">No techs yet. Add one to give them access.</p>}
        </div>
      )}
      <TechDialog tech={editing} databases={dbs} onClose={() => setEditing(null)}
        onSaved={(u, isNew) => { setEditing(null); load(); if (isNew && u.invite_token) setInviting(u); }} />
      <InviteLinkDialog tech={inviting} onClose={() => setInviting(null)} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={remove}
        title="Remove this tech?" description={`${toDelete?.name} will no longer be able to sign in. Their invoices stay.`} testId="delete-tech" />
    </div>
  );
}
